import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { debounce } from 'lodash';

import { selectCoursesPaginationSettings } from 'Config/configSlice';
import {
  createSearchParamsHash,
  decodeTagArray,
  QUERY_PARAM_EXCLUDE_TAGS,
  QUERY_PARAM_INCLUDE_TAGS,
  QUERY_PARAM_SEARCH
} from 'Courses/hooks/urlSyncUtils';
import { useAppDispatch, useAppSelector } from 'Store';

import { clearCourses, getCourses } from '../../../data';

const SEARCH_DEBOUNCE_MS = 1000;
const MIN_SEARCH_TEXT_LENGTH = 3;

export const useCoursesSearch = () => {
  const dispatch = useAppDispatch();
  const paginationSettings = useAppSelector(selectCoursesPaginationSettings);
  const [searchParams] = useSearchParams();

  const searchText = (searchParams.get(QUERY_PARAM_SEARCH) ?? '').trim();
  const includeTags = decodeTagArray(searchParams.get(QUERY_PARAM_INCLUDE_TAGS));
  const excludeTags = decodeTagArray(searchParams.get(QUERY_PARAM_EXCLUDE_TAGS));

  const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null);
  const currentSearchAbortControllerRef = useRef<AbortController | null>(null);
  const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSearchInProgress, setIsSearchInProgress] = useState(false);
  const lastProcessedParamsHashRef = useRef<string>('');
  const lastExecutedParamsHashRef = useRef<string>('');
  const hasBeenInitializedRef = useRef<boolean>(false);

  const hasSearchText = (text?: string): boolean => Boolean(text?.trim());

  const hasTagFilters = (include?: string[], exclude?: string[]): boolean =>
    Boolean((include?.length ?? 0) > 0 || (exclude?.length ?? 0) > 0);

  useEffect(
    () => () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }

      if (currentSearchAbortControllerRef.current) {
        currentSearchAbortControllerRef.current.abort();
      }

      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const apiRequestParams: {
      pageSize: number;
      page?: number;
      signal?: AbortSignal;
      searchText?: string;
      includeTags?: string[];
      excludeTags?: string[];
    } = {
      pageSize: paginationSettings.pageSize,
      page: undefined,
      signal: undefined
    };

    if (searchText?.trim()) {
      apiRequestParams.searchText = searchText.trim();
    }

    if (includeTags.length > 0) {
      apiRequestParams.includeTags = [...new Set(includeTags)];
    }

    if (excludeTags.length > 0) {
      apiRequestParams.excludeTags = [...new Set(excludeTags)];
    }

    const searchParamsHash = JSON.stringify({
      ...JSON.parse(
        createSearchParamsHash(
          apiRequestParams.searchText ?? '',
          apiRequestParams.includeTags ?? [],
          apiRequestParams.excludeTags ?? []
        )
      ),
      pageSize: apiRequestParams.pageSize
    });

    const hasValidSearchParams = Boolean(
      (apiRequestParams.searchText ?? false) ||
      (apiRequestParams.includeTags?.length ?? 0) > 0 ||
      (apiRequestParams.excludeTags?.length ?? 0) > 0
    );

    if (searchParamsHash === lastProcessedParamsHashRef.current) {
      return;
    }

    if (searchParamsHash === lastExecutedParamsHashRef.current) {
      return;
    }

    if (debouncedSearchRef.current && !hasSearchText(apiRequestParams.searchText)) {
      debouncedSearchRef.current.cancel();
    }

    if (currentSearchAbortControllerRef.current) {
      currentSearchAbortControllerRef.current.abort();
      currentSearchAbortControllerRef.current = null;
    }

    if (loaderTimeoutRef.current) {
      clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = null;
    }

    setIsSearchInProgress(false);

    lastProcessedParamsHashRef.current = searchParamsHash;

    const abortController = new AbortController();
    currentSearchAbortControllerRef.current = abortController;
    apiRequestParams.signal = abortController.signal;

    const executeSearch = () => {
      lastExecutedParamsHashRef.current = searchParamsHash;

      if (
        apiRequestParams.searchText &&
        apiRequestParams.searchText.length < MIN_SEARCH_TEXT_LENGTH
      ) {
        return;
      }

      dispatch(clearCourses());

      if (hasValidSearchParams) {
        setIsSearchInProgress(true);
      }

      const currentSearchParams = {
        pageSize: paginationSettings.pageSize,
        page: 1,
        searchText: apiRequestParams.searchText,
        includeTags: apiRequestParams.includeTags,
        excludeTags: apiRequestParams.excludeTags,
        signal: abortController.signal
      };

      dispatch(getCourses(currentSearchParams)).finally(() => {
        if (currentSearchAbortControllerRef.current === abortController) {
          setIsSearchInProgress(false);
          currentSearchAbortControllerRef.current = null;
        }
      });
    };

    const shouldExecuteSearch = hasValidSearchParams || hasBeenInitializedRef.current;

    if (shouldExecuteSearch) {
      if (hasSearchText(apiRequestParams.searchText)) {
        if (debouncedSearchRef.current) {
          debouncedSearchRef.current.cancel();
        }

        debouncedSearchRef.current = debounce(executeSearch, SEARCH_DEBOUNCE_MS);
        debouncedSearchRef.current();
      } else {
        executeSearch();
      }
    }

    hasBeenInitializedRef.current = true;
  }, [searchParams, paginationSettings.pageSize, dispatch]);

  const hasActiveSearch = Boolean(searchText || includeTags.length > 0 || excludeTags.length > 0);

  return { isSearchInProgress, hasActiveSearch } as const;
};
